/**
 * How each individual tier is presented across the chooser (A), paywall (B),
 * compare sheet, checkout review (C), billing (D) and the cancel journey.
 * Pure data + i18n key paths; components translate and render.
 *
 * Namespaces: keys under `onboarding.*` live in `auth`; keys under
 * `subscription.*` live in `common`. See COPY.md.
 */
import {
  getPaywallAgentModelForPlan,
  type AgentModelBrand,
} from '@tabbio/utils/agent-models';
import type { PlanTier } from '@tabbio/types';

export type JourneyTier = PlanTier;
export type PaidJourneyTier = Exclude<PlanTier, 'free'>;
/** Identity mark beside the tier name: Spark (Plus), flat Nova (Pro). */
export type TierCompanion = 'spark' | 'nova';
export type WritingLevelValue = 1 | 2 | 3;
export type ModelFamily = AgentModelBrand;

export type StackedLogoId =
  | 'chatgpt'
  | 'claude'
  | 'gemini'
  | 'perplexity'
  | 'gmail'
  | 'outlook'
  | 'whatsapp'
  | 'mcp'
  | 'cli';

/** Brand names are proper nouns: rendered as-is in both locales. */
export const STACKED_LOGO_NAMES: Record<StackedLogoId, string> = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  gemini: 'Gemini',
  perplexity: 'Perplexity',
  gmail: 'Gmail',
  outlook: 'Outlook',
  whatsapp: 'WhatsApp',
  mcp: 'MCP',
  cli: 'CLI',
};

export const MODEL_FAMILY_NAMES: Record<ModelFamily, string> = {
  gemini: 'Gemini',
  claude: 'Claude',
  openai: 'OpenAI',
};

export interface TierPresentation {
  tier: JourneyTier;
  /** Model family shown in the row; null for Free (no model logo). */
  model: ModelFamily | null;
  companion: TierCompanion | null;
  writingLevel: WritingLevelValue;
  /** `auth` namespace. */
  writingLabelKey: string;
  /** `common` namespace: billing summary tagline. */
  billingTaglineKey: string;
}

export const WRITING_LABEL_KEYS: Record<JourneyTier, string> = {
  free: 'onboarding.chooseAI.writing.basic',
  plus: 'onboarding.chooseAI.writing.enhanced',
  pro: 'onboarding.chooseAI.writing.advanced',
};

/**
 * The model family is read from the routing catalog, never hardcoded, so the
 * paywall cannot drift from what the tier actually runs. Requested design:
 * Plus = Gemini, Pro = Claude (pinned by a unit test). If the catalog ever
 * changes, the returned brand is shown.
 */
export function getTierModelFamily(tier: JourneyTier): ModelFamily | null {
  if (tier === 'free') return null;
  return getPaywallAgentModelForPlan(tier).brand;
}

export function getModelFamilyName(family: ModelFamily) {
  return MODEL_FAMILY_NAMES[family];
}

function buildPresentation(tier: JourneyTier): TierPresentation {
  return {
    tier,
    model: getTierModelFamily(tier),
    companion: tier === 'plus' ? 'spark' : tier === 'pro' ? 'nova' : null,
    writingLevel: tier === 'pro' ? 3 : tier === 'plus' ? 2 : 1,
    writingLabelKey: WRITING_LABEL_KEYS[tier],
    billingTaglineKey: `subscription.billing.tagline.${tier}`,
  };
}

export const PLAN_PRESENTATION: Record<JourneyTier, TierPresentation> = {
  free: buildPresentation('free'),
  plus: buildPresentation('plus'),
  pro: buildPresentation('pro'),
};

/** Chooser (A) row order. */
export const CHOOSER_TIERS: readonly JourneyTier[] = ['free', 'plus', 'pro'];
/** Paywall (B) tabs. */
export const PAYWALL_TIERS: readonly PaidJourneyTier[] = ['plus', 'pro'];

/** A: short benefits revealed under a selected paid row (`auth`). */
export const CHOOSER_BENEFIT_KEYS: Record<PaidJourneyTier, readonly string[]> = {
  plus: [
    'onboarding.chooseAI.benefits.plus.moreAi',
    'onboarding.chooseAI.benefits.plus.connectTools',
    'onboarding.chooseAI.benefits.plus.insightsCustomization',
  ],
  pro: [
    'onboarding.chooseAI.benefits.pro.everythingInPlus',
    'onboarding.chooseAI.benefits.pro.nova',
    'onboarding.chooseAI.benefits.pro.scheduled',
  ],
};

/**
 * B (24 September design): one Free-versus-selected-plan comparison table.
 * Row order and contents come from the design's `upgrade()`: CV writing
 * (model icon on the paid side), More AI usage (Plus) / Higher AI limits
 * (Pro), Pro-only Nova and Scheduled job searches, then the three shared
 * expandable rows (Connect your tools, Insights, More customization).
 * Labels and detail copy live under `subscription.paywall.*` (`common`).
 */
export type PaywallDetailKey = 'tools' | 'insights' | 'customization';
export interface PaywallComparisonRow {
  key: string;
  labelKey: string;
  /** Free column: included or not. The paid column is always included. */
  free: boolean;
  /** `model`: paid cell shows the tier's model icon; `detail`: expandable label. */
  kind: 'plain' | 'model' | 'detail';
  detail?: PaywallDetailKey;
}

export function getPaywallComparisonRows(tier: PaidJourneyTier): readonly PaywallComparisonRow[] {
  const rows: PaywallComparisonRow[] = [
    { key: 'cvWriting', labelKey: 'subscription.paywall.rows.cvWriting', free: true, kind: 'model' },
    tier === 'pro'
      ? { key: 'higherLimits', labelKey: 'subscription.paywall.rows.higherLimits', free: false, kind: 'plain' }
      : { key: 'moreAi', labelKey: 'subscription.paywall.rows.moreAi', free: false, kind: 'plain' },
  ];
  if (tier === 'pro') {
    rows.push(
      { key: 'nova', labelKey: 'subscription.paywall.rows.nova', free: false, kind: 'plain' },
      { key: 'scheduled', labelKey: 'subscription.paywall.rows.scheduled', free: false, kind: 'plain' },
    );
  }
  rows.push(
    { key: 'tools', labelKey: 'subscription.paywall.rows.tools', free: false, kind: 'detail', detail: 'tools' },
    { key: 'insights', labelKey: 'subscription.paywall.rows.insights', free: false, kind: 'detail', detail: 'insights' },
    { key: 'customization', labelKey: 'subscription.paywall.rows.customization', free: false, kind: 'detail', detail: 'customization' },
  );
  return rows;
}

/** "Connect your tools" detail: three named groups of sample connections. */
export const PAYWALL_TOOL_GROUPS: readonly {
  key: 'everyday' | 'ai' | 'developer';
  labelKey: string;
  logos: readonly StackedLogoId[];
}[] = [
  { key: 'everyday', labelKey: 'subscription.paywall.details.tools.everyday', logos: ['gmail', 'outlook', 'whatsapp'] },
  { key: 'ai', labelKey: 'subscription.paywall.details.tools.ai', logos: ['chatgpt', 'gemini', 'claude', 'perplexity'] },
  { key: 'developer', labelKey: 'subscription.paywall.details.tools.developer', logos: ['mcp', 'cli'] },
];
/** Closing line of the tools detail. */
export const PAYWALL_TOOLS_NOTE_KEY = 'subscription.paywall.details.tools.note';
/** One-paragraph details (`common`). */
export const PAYWALL_DETAIL_BODY_KEYS: Record<Exclude<PaywallDetailKey, 'tools'>, string> = {
  insights: 'subscription.paywall.details.insights',
  customization: 'subscription.paywall.details.customization',
};

/**
 * Compare sheet features (`common`). Keys ending in a model-bearing sentence
 * take `{{model}}` = `getModelFamilyName(getTierModelFamily(tier))`.
 */
export const COMPARE_FEATURE_KEYS: Record<JourneyTier, readonly string[]> = {
  free: [
    'subscription.compare.features.free.basicWriting',
    'subscription.compare.features.free.oneCv',
  ],
  plus: [
    'subscription.compare.features.plus.enhancedWriting',
    'subscription.compare.features.plus.moreAi',
    'subscription.compare.features.plus.connectTools',
    'subscription.compare.features.plus.insights',
    'subscription.compare.features.plus.customization',
  ],
  pro: [
    'subscription.compare.features.pro.everythingInPlus',
    'subscription.compare.features.pro.advancedWriting',
    'subscription.compare.features.pro.nova',
    'subscription.compare.features.pro.scheduled',
    'subscription.compare.features.pro.higherLimits',
  ],
};

/** Translation options for any key that interpolates the tier's model family. */
export function modelInterpolation(tier: PaidJourneyTier) {
  const family = getTierModelFamily(tier);
  return { model: family ? getModelFamilyName(family) : '' };
}
